/**
 * Coming Soon Banner Component
 * 
 * Displays a "Coming Soon" message with description and navigation links
 * to related functional screens. Used for screens where backend endpoints
 * are not yet available.
 * 
 * @module ui
 * @spec 102-screen-completeness
 * @story US4 - Support screens, US2 - Platform foundation
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Info } from 'lucide-react';

export interface RelatedLink {
  label: string;
  path: string;
  description?: string;
}

export interface ComingSoonBannerProps {
  /** Title of the feature */
  title: string;
  /** Description of what the feature will provide */
  description: string;
  /** Expected availability (e.g., "Q2 2026") */
  expectedAvailability?: string;
  /** Navigation links to related functional screens */
  relatedLinks?: RelatedLink[];
  /** Optional callback when user clicks feedback button */
  onFeedback?: () => void;
  /** Show as full-page or inline banner */
  variant?: 'page' | 'inline';
}

/**
 * ComingSoonBanner - Displays placeholder for features pending backend implementation
 * 
 * Per FR-US4-03 clarification: Shows navigation links to related functional screens
 * so users can access similar functionality while waiting for this feature.
 */
export const ComingSoonBanner: React.FC<ComingSoonBannerProps> = ({
  title,
  description,
  expectedAvailability,
  relatedLinks = [],
  onFeedback,
  variant = 'page',
}) => {
  const containerClasses = variant === 'page'
    ? 'flex flex-col items-center justify-center min-h-[400px] p-8'
    : 'p-6';

  return (
    <div 
      className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg ${containerClasses}`}
      role="status"
      aria-label="Feature coming soon"
    >
      <div className={variant === 'page' ? 'text-center max-w-lg' : ''}>
        {/* Icon */}
        <div className={`flex ${variant === 'page' ? 'justify-center' : ''} mb-4`}>
          <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-full">
            <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h2>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {description}
        </p>

        {/* Expected Availability */}
        {expectedAvailability && (
          <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-6 justify-center">
            <Info className="w-4 h-4" aria-hidden="true" />
            <span>Expected: {expectedAvailability}</span>
          </div>
        )}

        {/* Related Links */}
        {relatedLinks.length > 0 && (
          <div className={`${variant === 'page' ? 'mt-6' : 'mt-4'}`}>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Explore related screens:
            </p>
            <div className={`flex flex-wrap gap-2 ${variant === 'page' ? 'justify-center' : ''}`}>
              {relatedLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded-md transition-colors"
                  title={link.description}
                >
                  {link.label}
                  <ArrowRight className="w-3 h-3" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Feedback Button */}
        {onFeedback && (
          <button
            onClick={onFeedback}
            className="mt-6 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Request this feature
          </button>
        )}
      </div>
    </div>
  );
};

export default ComingSoonBanner;
